// Extracted from ProductsPage.js. Hardware variant
// selector dialog (screws / nails / lipping-patti / fasteners /
// moulding) with rate-type picker and inner multi-select cart.
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Plus, Trash2 } from 'lucide-react';
import { formatINR } from '../../../lib/store';
import { getHardwareRateOptions, getHardwareRate } from '../helpers';

const FRACTIONAL_RATE_TYPES = ['per kg', 'per running feet', 'per ltr', 'per gms', 'per ml'];

export function HardwareVariantSelectorDialog({
  group,
  onClose,
  selectedVariant,
  setSelectedVariant,
  selectedRateType,
  setSelectedRateType,
  quantityInput,
  setQuantityInput,
  setQuantity,
  cart,
  onAddToSelection,
  onRemoveFromSelection,
  onFinalize,
}) {
  return (
    <Dialog open={!!group} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="hardware-variant-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1A1C16]">Select Category</DialogTitle>
        </DialogHeader>

        {group && (
          <div className="space-y-4">
            {/* Product Info */}
            <div className="p-3 bg-[#FEF3C7] rounded-lg border border-[#F59E0B]/30">
              <p className="font-bold text-[#1A1C16]">{group.brand}</p>
              <p className="text-sm text-[#5C6150]">{group.name}</p>
              <span className="inline-block mt-2 text-xs bg-[#B45309]/20 text-[#B45309] px-2 py-1 rounded font-medium border border-[#B45309]/30 capitalize">
                {group.hardware_type}
              </span>
            </div>

            {/* Size/Variant Selector - Compact buttons like plywood */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#5C6150]">
                Select Size ({group.hardware_variants.length} available)
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {group.hardware_variants.map((hv, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setSelectedVariant(hv);
                      // Set default rate type for this variant
                      const options = getHardwareRateOptions(hv);
                      if (options.length > 0) {
                        setSelectedRateType(options[0].type);
                      }
                    }}
                    className={`flex flex-col items-center justify-center px-4 py-2 rounded-lg border transition-all ${
                      hv.nail_number ? 'min-w-[120px]' : 'min-w-[70px]'
                    } ${
                      selectedVariant?.size === hv.size && selectedVariant?.nail_number === hv.nail_number
                        ? 'border-[#B45309] bg-[#B45309] text-white'
                        : 'border-[#E2E6D9] bg-white hover:border-[#B45309]/50'
                    }`}
                    data-testid={`hardware-size-${hv.size}`}
                  >
                    {hv.nail_number ? (
                      /* Nail format: '12 NO - 2"' (inch symbol appended) */
                      <span className="font-mono text-sm font-semibold">{hv.nail_number} NO - {String(hv.size).includes('"') ? hv.size : `${hv.size}"`}</span>
                    ) : (
                      /* Regular format: size only (append inch for inch-unit types) */
                      <span className="font-mono text-sm font-semibold">{String(hv.size).includes('"') || !['screw', 'nail', 'lipping', 'fastener', 'moulding'].includes(group?.hardware_type) ? hv.size : `${hv.size}"`}</span>
                    )}
                    {hv.box_qty && (
                      <span className={`text-[10px] ${selectedVariant?.size === hv.size && selectedVariant?.nail_number === hv.nail_number ? 'text-white/80' : 'text-[#8F9485]'}`}>{hv.box_qty} pcs</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Rate Type Selector - Only show if variant has multiple rate options */}
            {selectedVariant && getHardwareRateOptions(selectedVariant).length > 1 && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-[#5C6150]">Select Rate Type</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {getHardwareRateOptions(selectedVariant).map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedRateType(opt.type)}
                      className={`p-2 text-sm rounded-md border transition-all ${
                        selectedRateType === opt.type
                          ? 'bg-[#B45309] text-white border-[#B45309]'
                          : 'bg-white text-[#1A1C16] border-[#E2E6D9] hover:border-[#B45309]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            {selectedVariant && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-[#5C6150]">
                  Quantity
                  {/* Decimal hint — hardware sold by weight (per kg / per running feet)
                      often goes in fractional amounts like 2.5 kg / 3.75 kg. */}
                  {FRACTIONAL_RATE_TYPES.includes(selectedRateType) && (
                    <span className="text-[10px] text-[#8F9485] normal-case ml-1">(decimals OK, e.g. 2.5)</span>
                  )}
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const step = FRACTIONAL_RATE_TYPES.includes(selectedRateType) ? 0.5 : 1;
                      const newQty = Math.max(step, (parseFloat(quantityInput) || 1) - step);
                      setQuantity(newQty);
                      setQuantityInput(String(newQty));
                    }}
                    className="h-10 w-10"
                  >-</Button>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={quantityInput}
                    onChange={(e) => {
                      // Allow digits + a single decimal point. Strip anything
                      // else so the user can't paste in junk.
                      let val = e.target.value.replace(/[^0-9.]/g, '');
                      // Collapse multiple dots to a single one.
                      const firstDot = val.indexOf('.');
                      if (firstDot !== -1) {
                        val = val.slice(0, firstDot + 1) + val.slice(firstDot + 1).replace(/\./g, '');
                      }
                      setQuantityInput(val);
                      if (val) {
                        setQuantity(parseFloat(val) || 1);
                      }
                    }}
                    onBlur={() => {
                      const f = parseFloat(quantityInput);
                      if (!quantityInput || isNaN(f) || f <= 0) {
                        setQuantityInput('1');
                        setQuantity(1);
                      }
                    }}
                    className="text-center h-10 w-20 font-bold text-lg"
                    data-testid="hardware-quantity-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const step = FRACTIONAL_RATE_TYPES.includes(selectedRateType) ? 0.5 : 1;
                      const newQty = (parseFloat(quantityInput) || 1) + step;
                      setQuantity(newQty);
                      setQuantityInput(String(newQty));
                    }}
                    className="h-10 w-10"
                  >+</Button>
                </div>
              </div>
            )}

            {/* Total Calculation */}
            {selectedVariant && selectedRateType && (
              <div className="p-3 bg-[#FEF3C7] rounded-lg border border-[#F59E0B]/30">
                <p className="text-xs text-[#B45309] uppercase tracking-wide mb-1">Total Calculation</p>
                <p className="text-sm text-[#5C6150]">
                  {formatINR(getHardwareRate(selectedVariant, selectedRateType))}/{selectedRateType.replace('per ', '')} × {parseFloat(quantityInput) || 1}
                </p>
                <p className="text-xl font-bold font-mono-data text-[#1A1C16] mt-1">
                  {formatINR(getHardwareRate(selectedVariant, selectedRateType) * (parseFloat(quantityInput) || 1))}
                </p>
              </div>
            )}

            {/* Add to Selection Button — accumulates the picked variant into
                the inner cart, so user can add several sizes/rate-types in
                one trip and commit them with one final click. */}
            <Button
              onClick={onAddToSelection}
              disabled={!selectedVariant || !selectedRateType}
              className="w-full bg-[#DAA520] hover:bg-[#B8860B] text-white uppercase tracking-wider font-semibold h-11 disabled:opacity-50"
              data-testid="hardware-add-to-selection-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add to Selection
            </Button>

            {/* Your Selection (running tally) */}
            {cart.length > 0 && (
              <div className="border-t border-[#E2E6D9] pt-3">
                <p className="text-xs text-[#5C6150] uppercase tracking-wide mb-2 font-semibold">
                  Your Selection ({cart.length} item{cart.length === 1 ? '' : 's'})
                </p>
                <div className="space-y-1.5 max-h-44 overflow-y-auto">
                  {cart.map((it, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 bg-[#F5F6F0] rounded px-2.5 py-1.5">
                      <div className="flex-1 min-w-0 text-xs">
                        <span className="font-mono font-semibold text-[#1A1C16]">
                          {(() => {
                            const sz = String(it.size).includes('"') ? it.size : `${it.size}"`;
                            return it.nail_number ? `${it.nail_number} NO - ${sz}` : sz;
                          })()}
                        </span>
                        <span className="text-[#5C6150]"> × {it.quantity} </span>
                        <span className="text-[#8F9485]">@ {formatINR(it.rate)}/{it.rateType.replace('per ', '')}</span>
                      </div>
                      <span className="font-mono-data text-xs font-semibold text-[#556B2F] whitespace-nowrap">
                        {formatINR(it.rate * it.quantity)}
                      </span>
                      <button
                        onClick={() => onRemoveFromSelection(i)}
                        className="text-[#B93838] hover:bg-red-50 rounded p-1"
                        aria-label="Remove from selection"
                        data-testid={`hardware-selection-remove-${i}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-[#E2E6D9]">
                  <span className="text-sm font-semibold text-[#5C6150]">Total</span>
                  <span className="font-mono-data text-base font-bold text-[#556B2F]">
                    {formatINR(cart.reduce((s, it) => s + it.rate * it.quantity, 0))}
                  </span>
                </div>
              </div>
            )}

            {/* Final commit button — only enabled when at least one item has been added */}
            <Button
              onClick={onFinalize}
              disabled={cart.length === 0}
              className="w-full bg-[#B45309] hover:bg-[#92400E] text-white uppercase tracking-wider font-semibold h-12 disabled:opacity-50"
              data-testid="confirm-hardware-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {cart.length > 0 ? `${cart.length} ` : ''}Item{cart.length === 1 ? '' : 's'} to Quotation
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
